import { useState, useEffect } from 'react';
import { Pencil } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';

interface EditableTitleProps {
  title: string;
  onSave: (newTitle: string) => void | Promise<void>;
  className?: string;
  titleClassName?: string;
}

export const EditableTitle: React.FC<EditableTitleProps> = ({
  title,
  onSave,
  className = '',
  titleClassName = '',
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);

  // Sync editedTitle when title prop changes
  useEffect(() => {
    if (!isDialogOpen) {
      setEditedTitle(title);
    }
  }, [title, isDialogOpen]);

  const handleOpenDialog = () => {
    setEditedTitle(title);
    setIsDialogOpen(true);
  };

  const handleCancel = () => {
    setEditedTitle(title);
    setIsDialogOpen(false);
  };

  const handleApply = async () => {
    if (editedTitle.trim() && editedTitle.trim() !== title) {
      await onSave(editedTitle.trim());
    }
    setIsDialogOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleApply();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <>
      <div
        className={`flex items-center gap-2 group ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <h1 className={`text-xl font-semibold text-foreground truncate ${titleClassName}`}>
          {title}
        </h1>
        <button
          onClick={handleOpenDialog}
          className={`opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
            isHovered ? 'opacity-100' : ''
          }`}
          aria-label="Edit title"
        >
          <Pencil className="h-4 w-4 text-muted-foreground hover:text-foreground" />
        </button>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Rename Chart</DialogTitle>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            <Input
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter chart name"
              autoFocus
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleApply} disabled={!editedTitle.trim()}>
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

